import { Brand, Lineup, Model, Trim } from '../types/common/car/CarInfo';
import { SelectInfo } from '../types/estimate/common/EstimateInfo';
import { Exemption } from '../types/estimate/common/TaxInfo';

/**
 *  숫자 3자리 콤마 추가 함수
 * @param {number | string} num 가격
 * @param {boolean} needZero 0의 출력이 필요 여부 (default: false)
 * @returns {string} 콤마가 추가된 숫자형태의 string
 */
export const addComma = (num: number | string, needZero = false): string => {
  if (needZero && (num === 0 || num === '0')) {
    return '0';
  }

  const number: number | string = Number(num);
  if (!number || isNaN(number)) return '';

  /* 문자열의 3자리마다 ,를 추가시키는 정규표현식 */
  // const regExp = /\B(?=(\d{3})+(?!\d))/g;

  //return Number(num).toString().replace(regExp, ',');
  return new Intl.NumberFormat().format(number);
};

/**
 *  문자열 bytes 계산 함수
 * @link (https://thingsthis.tistory.com/21)
 * @param {string} string 계산할 문자열
 * @return {number} 바이트
 *  */
export const calcBytes = (string: string): number => {
  let bytes: number = 0;

  for (let i: number = 0; i < string.length; ++i) {
    string.charCodeAt(i) > 127 ? (bytes += 2) : bytes++;
  }

  return bytes;
};

/**
 *  문자열의 일부를 잘라 그 뒤에 ...을 붙여 문자열로 반환하는 함수
 * @param {string} str 개조할 문자열
 * @param {number} length 표현할 문자 길이
 * @returns ...을 붙인 문자열 혹은 length 보다 작은 문자열의 길이일 경우엔 str
 *  */
export const addTripleDotName = (str: string, length: number): string => {
  return str.length >= length ? `${str.slice(0, length)}...` : str;
};

/**
 *  특정 객체의 특정 property를 제거하는 함수
 * @link https://codezup.com/10-ways-to-delete-remove-property-of-an-object-javascript/ -> no.7
 * @param {string} key 삭제할 property명
 * @param {object} object 대상 객체
 *  */
export const deleteKey = (key: string, { [key]: deletedKey, ...others }): any => others;

/**
 *  Array에서 특정 key 별 grouping 시켜줌
 * @link [https://jjeongil.tistory.com/1189]
 * @param {any[]} data 그룹핑에 필요한 배열
 * @param {string} key 그룹핑 해줄 키 명
 * @returns {object} 객체 형태로 키별 배열 형태로 반환
 */
export const groupBy = (data: any[], key: string) => {
  if (!data || data.length === 0) {
    return null;
  }

  return data.reduce((carry: any, el: any) => {
    let group = el[key];

    if (carry[group] === undefined) {
      carry[group] = [];
    }

    if (el.keyName) {
      /* 선택가격, 판매조건에서 사용 할 때 */
      if (!carry[group].sub) {
        carry[group].sub = [];
      }

      carry[group].groupName = el.groupName;
      carry[group].sub.push(el);
    } else {
      carry[group].push(el);
    }

    return carry;
  }, {});
};

/**
 *  배열의 특정 key(name)를 기준으로 오름차순 정리하는 함수
 * @param {T[]} list 정리할 배열
 * @param {string} name 오름차순 기준 key
 *  */
export const sortArray = <T>(list: T[], name: string): T[] => {
  if (!list) return [];

  const returnArr = list.sort((a: any, b: any) => {
    /* 오름차순 우선순위 설정 */
    const sortA: number = a[name];
    const sortB: number = b[name];

    return sortA - sortB;
  });

  return returnArr as T[];
};

/**
 *  현재의 문자가 대문자인지 구분해주는 함수
 * @param {string} letter 문자
 * @return {boolean} true면 대문자 false면 소문자
 *  */
export const isCapital = (letter: string): boolean => letter === letter.toUpperCase();

/**
 *  중복을 제거한 목록을 리턴하는 함수
 * @param {T[]} list 중복제거가 필요한 목록
 *  */
export const setArray = <T>(list: T[]): T[] => [...new Set(list.filter((item: T) => item))];

/**
 *  순수한 number를 리턴하는 함수
 * @param {string | number} number string or number 형태의 숫자
 *  */
export const getPureNumber = (number: number | string): number => {
  let returnNum = 0;
  if (typeof number === 'number') {
    returnNum = number;
  } else if (typeof number === 'string') {
    returnNum = Number(number.replace(/,/gi, ''));
  }

  return returnNum;
};

/**
 * 단위별로 절사 혹은 반올림, 올림을 할 수 있는 함수
 * @param {number} price 금액
 * @param {number} range 적용할 단위(1: 1의자리 수, 10: 10의자리 수 ... -1 => price를 리턴)
 * @param {string} type ROUND DOWN: 버림, ROUND: 반올림:, ROUND UP: 올림
 */
export const cuttingPrice = (price: number, range: number, type: string): number => {
  if (range === -1) {
    // range를 -1로 받으면 받은 금액 그대로 리턴
    return price;
  }

  const conceptRange: number = range * 10;
  const conceptPrice: number = price / conceptRange;
  let completePrice: number = 0;

  switch (type) {
    case '':
    case 'ROUND DOWN': // 버림
      completePrice = Math.floor(conceptPrice);
      break;

    case 'ROUND': // 반올림
      completePrice = Math.round(conceptPrice);
      break;

    case 'ROUND UP': // 올림
      completePrice = Math.ceil(conceptPrice);
      break;

    default:
      console.error('숫자 절사 타입이 잘못되었습니다.\n', { type });
      return 0;
  }

  // console.log(
  //   '가격 => ',
  //   price,
  //   '\n단위 => ',
  //   range,
  //   '\n타입 => ',
  //   type,
  //   '\n절사범위 => ',
  //   conceptRange,
  //   '\n금액/절사범위 => ',
  //   conceptPrice,
  //   '\n가공금액 => ',
  //   completePrice,
  //   '\n절사금액 (가공금액 * 절사범위 ) => ',
  //   completePrice * conceptRange
  // );

  return completePrice * conceptRange;
};

/**
 *  배열에 들어있는 숫자들 중에 targetNumber와 가장 가까운 숫자를 찾아내는 함수
 * @param {number[]} list 숫자가 들어있는 배열
 * @param {number} targetNumber 타겟번호
 *  */
export const getCloseToTargetNumber = (list: number[], targetNumber: number): number => {
  const closeNumber: number = list
    .filter(
      (num: number) =>
        Math.abs(num - targetNumber) ===
        Math.min(...list.map((no: number) => Math.abs(no - targetNumber)))
    )
    .sort((a: number, b: number) => a - b)[0];

  return closeNumber;
};

/**
 *  YYYY-MM-DD 일자를 문자열로 반환하는 함수
 *  */
export const getFullDate = (): string => {
  const date: Date = new Date();
  const fullDate: string =
    date.getFullYear() +
    '-' +
    (date.getMonth() + 1 > 9 ? date.getMonth() + 1 : `0${date.getMonth() + 1}`) +
    '-' +
    (date.getDate() > 9 ? date.getDate() : `0${date.getDate()}`);

  return fullDate;
};

/**
 *  페이지 뒤로가기시 모달만 닫히도록하는 함수
 * @param {function} closeEvent 모달 닫기 함수
 *  */
export const blockBack = (type: string, closeEvent: () => void): void => {
  // 뒤로가기 버튼 클릭 막기
  // const title: string = document.title;
  // history.pushState({ type }, '');
  history.pushState({ type }, '', window.location.href);
  window.onpopstate = (e: BeforeUnloadEvent) => {
    e.preventDefault();
    closeEvent();
    document.body.style.overflowY = 'auto';
  };
};

/**
 *  모델, 라인업, 트림과 연식(년형) 체크 여부에 의해 달라지는 통합 명칭과 연식을 리턴하는 함수
 * @param {Model} model 모델정보
 * @param {Lineup} lineup 라인업정보
 * @param {Trim} trim 트림정보
 * @param {Exemption} taxExemption 면세혜택 정보
 * @param {boolean} displayYear 연식(년형) 체크여부
 *  */
export const getTrimCombinationName = (
  model: Model | null,
  lineup: Lineup | null,
  trim: Trim | null,
  taxExemption: Exemption | null,
  displayYear: boolean
): {
  name: string;
  manufacturingYear: number;
} => {
  // 연식 정규표현식 ex) (2021NY)
  const yearRegex = /\(20\d{2}[a-zA-Z]{2}\)/;

  const modelName: string = model?.modelName || '';
  const lineupName: string = lineup?.lineupName.split(':')[0].replace(yearRegex, '') || '';
  const lineupExtra: RegExpMatchArray | null = lineup?.lineupName.match(yearRegex) || null;
  const trimName: string = displayYear
    ? yearRegex.test(lineup?.lineupName || '')
      ? `${trim?.trimName.split(')')[0]} ${lineupExtra && lineupExtra[0].replace(/\(|\)/g, '')})`
      : trim?.trimName || ''
    : trim?.trimName || '';
  const name: string = `${modelName} ${lineupName} ${trimName}${
    taxExemption?.newTaxExemptionTaxationTypeNo !== 1
      ? ` ${taxExemption?.newTaxExemptionTaxationTypeName}`
      : ''
  }`;

  // 연식
  const manufacturingYear: number = lineupExtra
    ? Number(lineupExtra[0].replace(/[^0-9]/g, '').replace(/\(|\)/g, ''))
    : 0;
  return {
    name,
    manufacturingYear,
  };
};

/**
 *  새로고침, 페이지 이동시 경고 alert을 띄워주기 위한 함수
 * @param {BeforeUnloadEvent} e BeforeUnloadEvent 객체
 * @param {SelectInfo<Brand>[]} brandInfo brandInfo recoild state
 * @param {string} title history state title
 * @link https://snupi.tistory.com/195
 *  */
export const preventClose = (
  e: BeforeUnloadEvent,
  brandInfo: SelectInfo<Brand>[],
  title: string
): void => {
  e.preventDefault();

  if (!!brandInfo.filter((info: SelectInfo<Brand>) => info.info).length) {
    e.returnValue = ''; //Chrome에서 동작하도록; deprecated
  }
  e.returnValue = ''; //Chrome에서 동작하도록; deprecated
};

/**
 * 앱으로 접속했는지에 대한 여부
 * @returns true: 앱 접속, false: 웹접속
 */
export const isApp = (): boolean => {
  return /solecar/i.test(window.navigator.userAgent);
};
